import { bianic, PropsOf, SystemProps, forwardRef } from "@bianic-ui/system"
import { omit, __DEV__ } from "@bianic-ui/utils"
import * as React from "react"
import { useImage, UseImageProps } from "./use-image"

interface NativeImageOptions {
  /**
   * The native HTML `width` attribute to the passed to the `img`
   */
  htmlWidth?: string | number
  /**
   * The native HTML `height` attribute to the passed to the `img`
   */
  htmlHeight?: string | number
}

interface NativeImageProps extends PropsOf<"img">, NativeImageOptions {}

const NativeImage = React.forwardRef(function NativeImage(
  props: NativeImageProps,
  ref: React.Ref<any>,
) {
  const { htmlWidth, htmlHeight, ...rest } = props
  return <img width={htmlWidth} height={htmlHeight} ref={ref} {...rest} />
})

interface ImageOptions extends NativeImageOptions {
  /**
   * Fallback image `src` to show if image is loading or image fails.
   *
   * Note 🚨: We recommend you use a local image
   */
  fallbackSrc?: string
  /**
   * Fallback element to show if image is loading or image fails.
   */
  fallback?: React.ReactElement
  /**
   * Defines loading strategy
   */
  loading?: "eager" | "lazy"
  /**
   * How the image to fit within it's bounds.
   * It maps to css `object-fit` property.
   */
  fit?: SystemProps["objectFit"]
  /**
   * How to align the image within its bounds.
   * It maps to css `object-position` property.
   */
  align?: SystemProps["objectPosition"]
  /**
   * If `true`, opt out of the `fallbackSrc` logic and use as `img`
   */
  ignoreFallback?: boolean
}

export interface ImageProps
  extends UseImageProps,
    Omit<PropsOf<typeof bianic.img>, keyof UseImageProps>,
    ImageOptions {}

/**
 * React component that renders an image with support
 * for fallbacks
 *
 * @see Docs https://bianic-ui.com/components/image
 */
export const Image = forwardRef<ImageProps, "img">(function Image(props, ref) {
  const {
    fallbackSrc,
    fallback,
    src,
    align,
    fit,
    loading,
    ignoreFallback,
    crossOrigin,
    ...rest
  } = props

  /**
   * Defer to native `img` tag if `loading` prop is passed
   * @see https://gitlab.com/bia-design/bianic-ui/issues/1027
   */
  const shouldIgnore = loading != null || ignoreFallback

  const status = useImage({
    ...props,
    ignoreFallback: shouldIgnore,
  })

  const shared = {
    ref,
    objectFit: fit,
    objectPosition: align,
    ...(shouldIgnore ? rest : omit(rest, ["onError", "onLoad"])),
  }

  if (status !== "loaded") {
    /**
     * If user passed a custom fallback component,
     * let's render it here.
     */
    if (fallback) return fallback

    return (
      <bianic.img
        as={NativeImage}
        className="bianic-image__placeholder"
        src={fallbackSrc}
        {...shared}
      />
    )
  }

  return (
    <bianic.img
      as={NativeImage}
      src={src}
      crossOrigin={crossOrigin}
      loading={loading}
      className="bianic-image"
      {...shared}
    />
  )
})

export interface ImgProps
  extends PropsOf<typeof bianic.img>,
    NativeImageOptions {}

/**
 * Fallback component for most SSR users who want to use the native `img` with
 * support for bianic props
 */
export const Img = forwardRef<ImgProps, "img">(function Img(props, ref) {
  return (
    <bianic.img
      ref={ref}
      as={NativeImage}
      className="bianic-image"
      {...props}
    />
  )
})

if (__DEV__) {
  Image.displayName = "Image"
}
